import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import type { NostrMetadata } from '@nostrify/nostrify';
import { UserRound } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WOT_INDEXER_RELAYS } from '@/lib/wot';

function httpsUrl(url: string | undefined): string | undefined {
  return url && /^https:\/\//i.test(url) ? url : undefined;
}

const compact = new Intl.NumberFormat('en', { notation: 'compact' });

/**
 * The highest-reach accounts found among the subject's sampled followers —
 * the strongest vouchers in their web. Each links to its own TrustWeb report.
 */
export function NotableFollowers({
  items,
}: {
  items: { pubkey: string; followers: number }[];
}) {
  const { nostr } = useNostr();
  const pubkeys = items.map((i) => i.pubkey);

  const { data: profiles } = useQuery({
    queryKey: ['notable-follower-profiles', pubkeys],
    enabled: pubkeys.length > 0,
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const events = await nostr
        .group([...WOT_INDEXER_RELAYS])
        .query([{ kinds: [0], authors: pubkeys, limit: pubkeys.length }], {
          signal: AbortSignal.any([signal, AbortSignal.timeout(8_000)]),
        })
        .catch(() => []);

      const map = new Map<string, NostrMetadata>();
      for (const event of events) {
        try {
          map.set(event.pubkey, JSON.parse(event.content) as NostrMetadata);
        } catch {
          // ignore malformed metadata
        }
      }
      return map;
    },
  });

  if (items.length === 0) return null;

  return (
    <Card className="bg-card/80 border-primary/30 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="font-display text-lg">Notable vouchers</CardTitle>
        <p className="text-sm text-muted-foreground">
          The most-followed accounts found in this identity's follower sample.
        </p>
      </CardHeader>
      <CardContent>
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map(({ pubkey, followers }) => {
            const meta = profiles?.get(pubkey);
            const name =
              meta?.display_name || meta?.name || `${pubkey.slice(0, 8)}…`;
            const npub = nip19.npubEncode(pubkey);
            return (
              <li key={pubkey}>
                <Link
                  to={`/${npub}`}
                  className="group flex flex-col items-center gap-2 rounded-lg border border-primary/20 bg-background/60 p-4 text-center transition-colors hover:border-primary/60 hover:bg-primary/10 outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Avatar className="size-12 border border-primary/40">
                    <AvatarImage src={httpsUrl(meta?.picture)} alt={name} />
                    <AvatarFallback className="bg-primary/20 text-glow">
                      <UserRound className="size-5" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="w-full truncate text-sm font-medium group-hover:text-glow transition-colors">
                    {name}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {compact.format(followers)} followers
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
